import React from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import PageHeader from '../components/common/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ExternalLink } from 'lucide-react';

interface PortfolioProject {
  name: string;
  url: string;
  image: string;
  description: string;
}

const portfolioProjects: PortfolioProject[] = [
  {
    name: "Discovery Quest",
    url: "https://discoveryquest.org/",
    image: "/lovable-uploads/portfolio-discoveryquest.png",
    description: "An interactive illustrated map experience for curious minds. Explore landmarks, find hidden objects, and learn about Aotearoa New Zealand — one discovery at a time. Available as laminated, folded, or digital maps."
  },
  {
    name: "The Inkerman Spa",
    url: "https://theinkermanspa.com/",
    image: "/lovable-uploads/portfolio-theinkermanspa.png",
    description: "Renwick's destination for beautifully crafted nails and a serene spa experience. Offering manicures, pedicures, nail art, with massage and beauty services coming soon."
  },
  {
    name: "How & Need",
    url: "https://howandneed.org/",
    image: "/lovable-uploads/portfolio-howandneed.png",
    description: "Smart grocery comparison platform helping NZ families save $25-$50 weekly. Compare supermarkets, get smart shopping plans, and track your savings with ease."
  },
  {
    name: "IdeaXroad",
    url: "https://ideaxroad.com/",
    image: "/lovable-uploads/portfolio-ideaxroad.png",
    description: "Your ideas deserve a verdict. Get a 6-minute data-backed, brutally honest GO/TEST/NO-GO report. Validate ideas quickly with actionable insights and clear direction."
  },
  {
    name: "Pattern & Signal",
    url: "https://patternandsignal.com/",
    image: "/lovable-uploads/portfolio-patternandsignal.png",
    description: "Strategic scenario observatory for New Zealand operators. Tracks macro signals, economic drivers and emerging shifts across industries with structured intelligence briefings and cycle-based analysis."
  },
  {
    name: "GuideDesk",
    url: "https://guidedesk.app/",
    image: "/lovable-uploads/portfolio-guidedesk.png",
    description: "Build and deploy a website assistant in minutes. Trained on your content for books, quotes, and answers automatically. Trusted by SMEs across NZ with a free forever plan."
  },
  {
    name: "A1 Hedge Trimming",
    url: "https://a1hedgetrimming.com/",
    image: "/lovable-uploads/portfolio-a1hedgetrimming.png",
    description: "Specialist macrocarpa and large hedge trimming for vineyards, farms, lifestyle blocks and rural properties across Marlborough. Fully insured with specialist equipment and visible before-and-after results."
  },
  {
    name: "Garden City Insulation",
    url: "https://gardencityinsulation.com/",
    image: "/lovable-uploads/portfolio-gardencityinsulation.png",
    description: "Christchurch-based insulation installers working across the entire South Island. Ceiling, wall and underfloor insulation for new builds, existing homes and rentals — send plans or measurements and the team reviews the job and prepares a quote."
  },
  {
    name: "Trees for Marlborough",
    url: "https://treesformarlborough.org/",
    image: "/lovable-uploads/portfolio-treesformarlborough.png",
    description: "Restoring nature, growing people, strengthening community. Supporting Marlborough's forestation while providing employment and training opportunities for people with disabilities."
  },
  {
    name: "Selmes Charitable Trust",
    url: "https://selmes.co.nz/",
    image: "/lovable-uploads/portfolio-selmes.png",
    description: "Changing minds, changing lives. Native plant nursery creating meaningful work, training, and genuine purpose for people with disabilities in Marlborough."
  },
  {
    name: "Property Optimisation",
    url: "https://property-optimisation.lovable.app/start",
    image: "/lovable-uploads/portfolio-propertyoptimisation.png",
    description: "Free property snapshot tool for NZ homeowners. Uses publicly available data — local zoning, property size, regional rentals, and environmental info — to surface income, savings, and lifestyle opportunities your property may already be sitting on."
  }
];

const Portfolio: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Public Portfolio | ThinkTomorrow.AI NZ Projects</title>
        <meta name="description" content="See selected websites, apps, and AI projects ThinkTomorrow has built for New Zealand businesses and community organisations." />
        <link rel="canonical" href="https://thinktomorrow.ai/portfolio" />
        <meta property="og:title" content="Public Portfolio | ThinkTomorrow.AI" />
        <meta property="og:description" content="Selected websites, apps, and AI projects built for NZ businesses." />
        <meta property="og:url" content="https://thinktomorrow.ai/portfolio" />
        <meta property="og:type" content="website" />
      </Helmet>
      <Navbar />
      <main className="min-h-screen pt-16">
        <PageHeader 
          title="Public Portfolio"
          titleNote="( Public platforms and websites )"
          subtitle="Showcasing our work helping NZ businesses and organisations thrive with innovative digital solutions."
        />
        
        <div className="section-spacing">
          <div className="section-inner">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {portfolioProjects.map((project, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <a href={project.url} target="_blank" rel="noopener noreferrer" className="block">
                    <div className="aspect-video w-full overflow-hidden bg-muted">
                      <img 
                        src={project.image} 
                        alt={`${project.name} screenshot`}
                        className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  </a>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      {project.name}
                      <a 
                        href={project.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-brand-teal hover:text-brand-teal/80 transition-colors"
                      >
                        <ExternalLink className="h-5 w-5" />
                      </a>
                    </CardTitle>
                    <CardDescription className="text-base">
                      {project.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Portfolio;
